import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatCurrency, toISODate } from "../utils";

const DEFAULT_TAX = "19";

const createEmptyPosition = () => ({
  description: "",
  quantity: "1",
  unitPrice: "0.00",
  taxPercent: DEFAULT_TAX,
});

function parseDecimal(value) {
  if (value == null) return null;
  const normalized = String(value).replace(",", ".").trim();
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function InvoiceDialog({
  open,
  onClose,
  onSubmit,
  initialInvoice,
  order,
  isSubmitting,
}) {
  const [number, setNumber] = useState("");
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [positions, setPositions] = useState(() => [createEmptyPosition()]);
  const [formError, setFormError] = useState("");
  const [positionErrors, setPositionErrors] = useState([]);

  useEffect(() => {
    if (open) {
      setNumber(initialInvoice?.number ?? "");
      setDate(initialInvoice?.date ?? toISODate(new Date()));
      setPositions([createEmptyPosition()]);
      setFormError("");
      setPositionErrors([]);
    }
  }, [open, initialInvoice]);

  const totals = useMemo(() => {
    let net = 0;
    let tax = 0;
    positions.forEach((position) => {
      const quantity = parseDecimal(position.quantity);
      const unitPrice = parseDecimal(position.unitPrice);
      const taxPercent = parseDecimal(position.taxPercent);
      if (quantity == null || unitPrice == null || taxPercent == null) return;
      const lineNet = quantity * unitPrice;
      const lineTax = lineNet * (taxPercent / 100);
      net += lineNet;
      tax += lineTax;
    });
    const roundedNet = Math.round(net * 100) / 100;
    const roundedTax = Math.round(tax * 100) / 100;
    const roundedGross = Math.round((roundedNet + roundedTax) * 100) / 100;
    return { net: roundedNet, tax: roundedTax, gross: roundedGross };
  }, [positions]);

  const updatePosition = (index, field, value) => {
    setPositions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setPositionErrors((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], [field]: false };
      }
      return next;
    });
  };

  const addPosition = () => {
    setPositions((prev) => [...prev, createEmptyPosition()]);
    setPositionErrors((prev) => [...prev, { description: false, quantity: false, unitPrice: false, taxPercent: false }]);
  };

  const removePosition = (index) => {
    setPositions((prev) => {
      if (prev.length === 1) return prev;
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    setPositionErrors((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormError("");

    const trimmedNumber = number.trim();
    if (!trimmedNumber) {
      setFormError("Bitte eine Rechnungsnummer angeben.");
      return;
    }
    if (!date) {
      setFormError("Bitte ein Rechnungsdatum auswählen.");
      return;
    }

    const errors = positions.map(() => ({
      description: false,
      quantity: false,
      unitPrice: false,
      taxPercent: false,
    }));
    const sanitized = [];
    let hasError = false;

    positions.forEach((position, index) => {
      const description = position.description.trim();
      const quantity = parseDecimal(position.quantity);
      const unitPrice = parseDecimal(position.unitPrice);
      const taxPercent = parseDecimal(position.taxPercent);

      if (!description) {
        errors[index].description = true;
        hasError = true;
      }
      if (quantity == null || quantity <= 0) {
        errors[index].quantity = true;
        hasError = true;
      }
      if (unitPrice == null || unitPrice < 0) {
        errors[index].unitPrice = true;
        hasError = true;
      }
      if (taxPercent == null || taxPercent < 0) {
        errors[index].taxPercent = true;
        hasError = true;
      }

      sanitized.push({
        description,
        quantity: quantity ?? 0,
        unitPrice: unitPrice ?? 0,
        taxPercent: taxPercent ?? 0,
      });
    });

    setPositionErrors(errors);

    if (hasError) {
      setFormError("Bitte alle Positionen vollständig ausfüllen.");
      return;
    }

    onSubmit({
      number: trimmedNumber,
      date,
      positions: sanitized,
    });
  };

  const disableClose = isSubmitting;

  return (
    <Dialog open={open} onClose={disableClose ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Rechnung erstellen</DialogTitle>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          <Stack spacing={3}>
            {initialInvoice && (
              <Alert severity="info">
                Es ist bereits eine Rechnung gespeichert. Eine neue Version überschreibt die bestehende Datei.
              </Alert>
            )}

            {order && (
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Auftrag</Typography>
                <Typography variant="body2" color="text.secondary">
                  {order.description || "Ohne Beschreibung"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ID: {order.id}
                </Typography>
              </Stack>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                required
                label="Rechnungsnummer"
                value={number}
                onChange={(event) => setNumber(event.target.value)}
                disabled={isSubmitting}
              />
              <TextField
                required
                label="Rechnungsdatum"
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                sx={{ minWidth: 200 }}
                InputLabelProps={{ shrink: true }}
                disabled={isSubmitting}
              />
            </Stack>

            {formError && <Alert severity="error">{formError}</Alert>}

            <div>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1">Positionen</Typography>
                <Button startIcon={<AddIcon />} variant="outlined" onClick={addPosition} disabled={isSubmitting}>
                  Position hinzufügen
                </Button>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell align="right">Menge</TableCell>
                    <TableCell align="right">Einzelpreis (€)</TableCell>
                    <TableCell align="right">MwSt. (%)</TableCell>
                    <TableCell align="right">Netto (€)</TableCell>
                    <TableCell align="right">Brutto (€)</TableCell>
                    <TableCell align="right">&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map((position, index) => {
                    const quantity = parseDecimal(position.quantity);
                    const unitPrice = parseDecimal(position.unitPrice);
                    const taxPercent = parseDecimal(position.taxPercent);
                    const lineNet = quantity != null && unitPrice != null ? quantity * unitPrice : null;
                    const lineTax = lineNet != null && taxPercent != null ? lineNet * (taxPercent / 100) : null;
                    const lineGross = lineNet != null && lineTax != null ? lineNet + lineTax : null;
                    const errors = positionErrors[index] || {};

                    return (
                      <TableRow key={index}>
                        <TableCell sx={{ minWidth: 220 }}>
                          <TextField
                            fullWidth
                            required
                            size="small"
                            placeholder="z. B. Inspektion"
                            value={position.description}
                            onChange={(event) => updatePosition(index, "description", event.target.value)}
                            error={Boolean(errors.description)}
                            disabled={isSubmitting}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: 120 }}>
                          <TextField
                            required
                            size="small"
                            inputProps={{ inputMode: "decimal", min: "0", step: "0.1" }}
                            value={position.quantity}
                            onChange={(event) => updatePosition(index, "quantity", event.target.value)}
                            error={Boolean(errors.quantity)}
                            disabled={isSubmitting}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: 150 }}>
                          <TextField
                            required
                            size="small"
                            inputProps={{ inputMode: "decimal", min: "0", step: "0.01" }}
                            value={position.unitPrice}
                            onChange={(event) => updatePosition(index, "unitPrice", event.target.value)}
                            error={Boolean(errors.unitPrice)}
                            disabled={isSubmitting}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: 140 }}>
                          <TextField
                            required
                            size="small"
                            inputProps={{ inputMode: "decimal", min: "0", step: "0.1" }}
                            value={position.taxPercent}
                            onChange={(event) => updatePosition(index, "taxPercent", event.target.value)}
                            error={Boolean(errors.taxPercent)}
                            disabled={isSubmitting}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: 140 }}>
                          <Typography variant="body2">{lineNet != null ? formatCurrency(lineNet) : "—"}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ width: 140 }}>
                          <Typography variant="body2">{lineGross != null ? formatCurrency(lineGross) : "—"}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ width: 80 }}>
                          <IconButton
                            color="error"
                            onClick={() => removePosition(index)}
                            disabled={isSubmitting || positions.length === 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <Divider />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Nettosumme</Typography>
                <Typography variant="h6">{formatCurrency(totals.net)}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">MwSt.</Typography>
                <Typography variant="h6">{formatCurrency(totals.tax)}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Bruttosumme</Typography>
                <Typography variant="h6">{formatCurrency(totals.gross)}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={disableClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? "Speichern…" : "Als PDF generieren & speichern"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
